import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useMemo, useState } from 'react'
import { DatePickerDateValue, DateRangePickerProps } from './DateRangePicker.types'
import { buildCalendarDays } from './DateRangePicker.utils'

interface DateRangePickerControllerProps extends Pick<
  DateRangePickerProps,
  'value' | 'onChange' | 'labels' | 'singleDate' | 'defaultCalendarMonth'
> {}
export const useDateRangePickerController = ({
  value,
  onChange,
  labels = [],
  singleDate = false,
  defaultCalendarMonth,
}: DateRangePickerControllerProps) => {
  const initialCalendarMonth = useMemo(() => {
    if (value.startDate) return dayjs(value.startDate)
    if (defaultCalendarMonth) return dayjs(defaultCalendarMonth)
    return dayjs()
  }, [value.startDate, defaultCalendarMonth])

  const [currentMonth, setCurrentMonth] = useState(initialCalendarMonth)
  const [hoverDate, setHoverDate] = useState<DatePickerDateValue>(null)
  const [showYearSelect, setShowYearSelect] = useState(false)
  const [showMonthSelect, setShowMonthSelect] = useState(false)
  const { startDate, endDate } = value

  const onPrevMonth = useCallback(() => {
    setCurrentMonth(currentMonth.subtract(1, 'month'))
  }, [currentMonth])

  const onNextMonth = useCallback(() => {
    setCurrentMonth(currentMonth.add(1, 'month'))
  }, [currentMonth])

  const onShowYearSelector = useCallback(() => {
    setShowYearSelect(true)
    setShowMonthSelect(false)
  }, [])

  const onDateSelect = useCallback(
    (date: Dayjs) => {
      if (!startDate || singleDate) {
        onChange({ startDate: date, endDate: null })
        return
      }
      if (!endDate) {
        if (date.isAfter(startDate)) {
          onChange({ startDate, endDate: date })
        } else {
          onChange({ startDate: date, endDate: startDate })
        }
        return
      }

      onChange({ startDate: date, endDate: null })
    },
    [endDate, onChange, singleDate, startDate],
  )

  const onYearSelect = useCallback(
    (year: number) => {
      setCurrentMonth(currentMonth.year(year))
      setShowYearSelect(false)
      setShowMonthSelect(true)
    },
    [currentMonth],
  )

  const onMonthSelect = useCallback(
    (year: number) => {
      setCurrentMonth(currentMonth.month(year))
      setShowMonthSelect(false)
    },
    [currentMonth],
  )

  const weekDays = useMemo(() => {
    const ld = dayjs().localeData()
    const first = ld.firstDayOfWeek()
    const names = ld.weekdaysMin()
    return [...names.slice(first), ...names.slice(0, first)]
  }, [])

  const days = useMemo(() => buildCalendarDays(currentMonth), [currentMonth])

  const labelsMap = useMemo(() => {
    const map = new Map<string, DateRangePickerProps['labels']>()
    labels.forEach((label) => {
      const key = dayjs(label.date).format('YYYY-MM-DD')
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(label)
    })
    return map
  }, [labels])

  return {
    startDate,
    endDate,
    onDateSelect,
    hoverDate,
    setHoverDate,
    currentMonth,
    onPrevMonth,
    onNextMonth,
    weekDays,
    days,
    labelsMap,
    showYearSelect,
    onShowYearSelector,
    onYearSelect,
    showMonthSelect,
    onMonthSelect,
  }
}
